import * as React from 'react';

import useTracker from '@coursera/event-pulse/react/useTracker';

import useHasDayOneOnboardingModalBeenSeen from 'bundles/course-v2/components/day-one-onboarding-modal/hooks/useHasDayOneOnboardingModalBeenSeen';

type DayOneOnboardingModalContextType = {
  isOpen: boolean;
  openModal: (firstItemId?: string) => void;
  closeModal: () => void;
  firstItemId?: string;
  hasBeenSeenInPreviousSession: boolean;
  hasBeenSeenInPreviousSessionIsLoading: boolean;
  hasBeenSeenInThisSession: boolean;
  courseBranchId?: string;
  hasLoadedFirstItem: boolean;
  enableDayOneOnboardingIsLoading: boolean;
  setEnableDayOneOnboardingIsLoading: (isLoading: boolean) => void;
  hasMadeProgressInCourse: boolean;
};
const DayOneOnboardingModalContext = React.createContext<DayOneOnboardingModalContextType>({
  isOpen: false,
  openModal: () => {},
  closeModal: () => {},
  firstItemId: undefined,
  courseBranchId: undefined,
  /**
   * Session in this context refers to the time the learner has been on the page since
   * the page was loaded. It is not the same as the session in the context of the browser.
   */
  hasBeenSeenInPreviousSession: false,
  hasBeenSeenInPreviousSessionIsLoading: false,
  hasBeenSeenInThisSession: false,
  hasLoadedFirstItem: false,
  enableDayOneOnboardingIsLoading: false,
  setEnableDayOneOnboardingIsLoading: () => {},
  hasMadeProgressInCourse: false,
});

type DayOneOnboardingModalProviderProps = {
  children: React.ReactNode;
  courseBranchId: string;
  hasMadeProgressInCourse: boolean;
  nextItemId?: string;
};

const DayOneOnboardingModalProvider = ({
  children,
  courseBranchId,
  hasMadeProgressInCourse,
  nextItemId,
}: DayOneOnboardingModalProviderProps) => {
  // Called here as we want to make the query as early as possible
  const { hasBeenSeen: hasBeenSeenInPreviousSession, loading: hasBeenSeenInPreviousSessionIsLoading } =
    useHasDayOneOnboardingModalBeenSeen();
  const track = useTracker();

  const [isOpen, setIsOpen] = React.useState(false);
  const [hasLoadedFirstItem, setHasLoadedFirstItem] = React.useState<boolean>(false);
  const [isEnableDayOneOnboardingLoading, setIsEnableDayOneOnboardingLoading] = React.useState<boolean>(false);
  const [hasBeenSeenInThisSession, setHasBeenSeenInThisSession] = React.useState<boolean>(false);
  const [cachedFirstItemId, setCachedFirstItemId] = React.useState<string | undefined>(undefined);
  const openModal = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (firstItemId?: string) => {
      setIsOpen(true);
      if (firstItemId) {
        setHasLoadedFirstItem(true);
        setCachedFirstItemId(firstItemId);
      }
    },
    [setCachedFirstItemId, setIsOpen]
  );

  const closeModal = React.useCallback<() => $TSFixMe>(() => {
    setIsOpen(false);
    // We set this on close to ensure that the modal is not shown again
    // Instead of calling the api in case the call to set the event is not successful
    // or hasn't returned yet
    setHasBeenSeenInThisSession(true);
    track('close_modal', {
      modal: { name: 'course_day_one_onboarding_benchmark_screen' },
      courseItem: {
        id: cachedFirstItemId,
      },
      ...(courseBranchId
        ? {
            courseBranch: {
              id: courseBranchId,
            },
          }
        : {}),
    });
  }, [setIsOpen, track, cachedFirstItemId, courseBranchId]);

  React.useEffect(() => {
    if (nextItemId) {
      setHasLoadedFirstItem(true);
      setCachedFirstItemId(nextItemId);
    }
  }, [setHasLoadedFirstItem, setCachedFirstItemId, nextItemId]);

  /**
   * Sets the loading state for enabling DayOneOnboarding. This is a side effect
   * because it conditionally updates the context state *only* when the modal
   * hasn't been seen in the previous session or the current session. If the user
   * has already seen the modal, we do nothing here, just to make sure only the
   * right learners see the loading icon
   */
  const setEnableDayOneOnboardingIsLoading = React.useCallback<(x: boolean) => void>(
    (isLoading: boolean) => {
      if (!hasBeenSeenInPreviousSession && !hasBeenSeenInPreviousSessionIsLoading && !hasBeenSeenInThisSession) {
        setIsEnableDayOneOnboardingLoading(isLoading);
      }
    },
    [hasBeenSeenInPreviousSession, hasBeenSeenInPreviousSessionIsLoading, hasBeenSeenInThisSession]
  );

  return (
    <DayOneOnboardingModalContext.Provider
      value={{
        isOpen,
        openModal,
        closeModal,
        firstItemId: cachedFirstItemId,
        hasBeenSeenInPreviousSession,
        hasBeenSeenInPreviousSessionIsLoading,
        hasBeenSeenInThisSession,
        courseBranchId,
        hasLoadedFirstItem,
        enableDayOneOnboardingIsLoading: isEnableDayOneOnboardingLoading,
        setEnableDayOneOnboardingIsLoading,
        hasMadeProgressInCourse,
      }}
    >
      {children}
    </DayOneOnboardingModalContext.Provider>
  );
};

const useDayOneOnboardingModal = () => React.useContext(DayOneOnboardingModalContext);

export { DayOneOnboardingModalProvider, useDayOneOnboardingModal };
